import React, { useCallback } from 'react';
import { type Href, Link, useSegments } from 'expo-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  Navbar,
  theme,
  useNavbarContainerHeight,
  useResponsive,
  useToggleState,
  VStack,
} from '@sg/garnish';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useBagCount, usePendingOrder } from '../../../../hooks';
import { useActiveScreenOptions } from '../../../../routing/hooks/useActiveScreenOptions';
import { LINKS, NAVIGATION_CONTENT_TYPE, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { useNavbarLocationData } from './hooks';

import type { Asset, Entry } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarSM = () => {
  const segments = useSegments();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { minWidth } = useResponsive();
  const { heightStyle: navbarHeightStyle } = useNavbarContainerHeight();

  const { bagCount } = useBagCount();
  const pendingOrder = usePendingOrder({ requestPolicy: 'cache-only' });
  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();
  const { locationEyebrow, locationTitle, menuHref } = useNavbarLocationData({ lastInteractedStore }); // prettier-ignore

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isMoreOpen,
    toggle: handleMorePress,
    toggleOff: handleMoreLinkPress,
  } = useToggleState();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data: navigationData } = useContentfulContentTypeEntry<NavigationContent>({
    contentType: NAVIGATION_CONTENT_TYPE,
    include: 2,
  });

  const moreMenuNavLinks = navigationData?.fields?.moreMenuNavLinks ?? [];

  // ─── Navigation ──────────────────────────────────────────────────────

  const screenOptions = useActiveScreenOptions();
  const navbarLayout = screenOptions?.navbarLayout ?? 'floating';

  const shouldRenderStaticContainer = navbarLayout !== 'floating';
  const shouldRenderStaticContainerBorder = navbarLayout === 'static-with-border';
  const shouldShowGlassShadow = navbarLayout === 'floating';
  const shouldRenderCompactNav = !minWidth.isMD;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isMenuActive = segments.some((segment) => segment === TABS.MENU);
  const isRewardsActive = segments.some((segment) => segment === TABS.REWARDS);
  const isHomeActive = segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE); // prettier-ignore

  const shouldShowCurrentOrder = isHomeActive && Boolean(pendingOrder);

  // ─── Styles ─────────────────────────────────────────────────────────

  const staticNavbarContainerStyles = [
    styles.staticNavbarContainer,
    shouldRenderStaticContainerBorder ? styles.staticNavbarContainerBorder : undefined,
    navbarHeightStyle,
  ];

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleHomePress = useCallback(() => {
    handleMoreLinkPress();
    track('home.jump_nav');
  }, [handleMoreLinkPress, track]);

  const handleMenuPress = useCallback(() => {
    handleMoreLinkPress();
    track('menu.jump_nav', {});
  }, [handleMoreLinkPress, track]);

  const handleRewardsPress = useCallback(() => {
    handleMoreLinkPress();
    track('loyalty.jump_nav');
  }, [handleMoreLinkPress, track]);

  const handleBagPress = useCallback(() => {
    handleMoreLinkPress();
    track('bag.jump_nav');
  }, [handleMoreLinkPress, track]);

  const handleCurrentOrderPress = useCallback(() => {
    handleMoreLinkPress();
    pendingOrder?.onPress();
  }, [handleMoreLinkPress, pendingOrder]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Navbar.Floating.Container>
        {shouldRenderCompactNav ? (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Navbar.Floating.MenuButton
              accessibilityLabel={formatMessage(messages.openNavigation)}
              onPress={handleMorePress}
            />

            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>
          </Navbar.Floating.PrimaryNav>
        ) : (
          <Navbar.Floating.PrimaryNav shadow={shouldShowGlassShadow}>
            <Link
              href="/"
              asChild
              disabled={isHomeActive}
              aria-label={formatMessage(messages.homeAccessibilityLabel)}
            >
              <Navbar.Floating.LogoPressable onPress={handleHomePress}>
                <Navbar.Floating.Logo />
              </Navbar.Floating.LogoPressable>
            </Link>

            <Navbar.Floating.NavLinksList>
              <Link asChild href={menuHref}>
                <Navbar.Floating.Link
                  isActive={isMenuActive && !isMoreOpen}
                  onPress={handleMenuPress}
                >
                  <FormattedMessage {...messages.menu} />
                </Navbar.Floating.Link>
              </Link>

              <Link asChild href={LINKS.REWARDS}>
                <Navbar.Floating.Link
                  isActive={isRewardsActive && !isMoreOpen}
                  onPress={handleRewardsPress}
                >
                  <FormattedMessage {...messages.rewards} />
                </Navbar.Floating.Link>
              </Link>

              <Navbar.Floating.Link
                accessibilityRole="button"
                accessibilityState={{ expanded: isMoreOpen }}
                isActive={isMoreOpen}
                onPress={handleMorePress}
              >
                <FormattedMessage {...messages.more} />
              </Navbar.Floating.Link>
            </Navbar.Floating.NavLinksList>
          </Navbar.Floating.PrimaryNav>
        )}

        {isMoreOpen && shouldRenderCompactNav ? (
          <Navbar.Floating.Drawer.Container
            accessibilityLabel={formatMessage(messages.navigationMenu)}
            backdropAccessibilityLabel={formatMessage(messages.closeNavigationBackdrop)}
            closeAccessibilityLabel={formatMessage(messages.closeNavigation)}
            onClose={handleMoreLinkPress}
          >
            <Navbar.Floating.Drawer.VStack>
              <Navbar.Floating.Drawer.NavLinksList>
                <Link asChild href={menuHref}>
                  <Navbar.Floating.Drawer.NavLink onPress={handleMenuPress}>
                    <FormattedMessage {...messages.menu} />
                  </Navbar.Floating.Drawer.NavLink>
                </Link>

                <Link asChild href={LINKS.REWARDS}>
                  <Navbar.Floating.Drawer.NavLink onPress={handleRewardsPress}>
                    <FormattedMessage {...messages.rewards} />
                  </Navbar.Floating.Drawer.NavLink>
                </Link>
              </Navbar.Floating.Drawer.NavLinksList>

              {moreMenuNavLinks.length > 0 ? (
                <Navbar.Floating.Drawer.NavLinksList>
                  {moreMenuNavLinks.map((link) => (
                    <Link key={link.sys.id} asChild href={link.fields.url as Href}>
                      <Navbar.Floating.Drawer.NavLink onPress={handleMoreLinkPress}>
                        {link.fields.title}
                      </Navbar.Floating.Drawer.NavLink>
                    </Link>
                  ))}
                </Navbar.Floating.Drawer.NavLinksList>
              ) : null}
            </Navbar.Floating.Drawer.VStack>

            <Navbar.Floating.Drawer.Footer>
              {isLoggedIn ? (
                <Navbar.Floating.Drawer.NavLinksList hasDivider>
                  <Link asChild href={LINKS.ORDER_HISTORY}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreLinkPress}>
                      <FormattedMessage {...messages.viewOrderHistory} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>

                  <Link asChild href={LINKS.ACCOUNT}>
                    <Navbar.Floating.Drawer.NavLink onPress={handleMoreLinkPress}>
                      <FormattedMessage {...messages.myAccount} />
                    </Navbar.Floating.Drawer.NavLink>
                  </Link>
                </Navbar.Floating.Drawer.NavLinksList>
              ) : (
                <Link asChild href={LINKS.AUTH} onPress={handleMoreLinkPress}>
                  <Button accessibilityRole="link" size="large">
                    <BodyText sizeMatch={['16']} bold style={styles.drawerAuthLabel}>
                      <FormattedMessage {...messages.signInCreateAccount} />
                    </BodyText>
                  </Button>
                </Link>
              )}
            </Navbar.Floating.Drawer.Footer>
          </Navbar.Floating.Drawer.Container>
        ) : null}

        {isMoreOpen && !shouldRenderCompactNav ? (
          <Navbar.Floating.Popup
            shadow={shouldShowGlassShadow}
            onClose={handleMoreLinkPress}
            accessibilityLabel={formatMessage(messages.moreMenu)}
          >
            <VStack gap={0}>
              {moreMenuNavLinks.map((link) => (
                <Link key={link.sys.id} asChild href={link.fields.url as Href}>
                  <Navbar.Floating.PageLink
                    title={link.fields.title}
                    description={link.fields.text}
                    imageSrc={link.fields.image.fields.file.url}
                    imageAlt={link.fields.image.fields.description}
                    onPress={handleMoreLinkPress}
                  />
                </Link>
              ))}
            </VStack>

            {isLoggedIn ? (
              <Navbar.Floating.PopupFooter>
                <Link asChild href={LINKS.ORDER_HISTORY}>
                  <Navbar.Floating.TextLink onPress={handleMoreLinkPress}>
                    <FormattedMessage {...messages.viewOrderHistory} />
                  </Navbar.Floating.TextLink>
                </Link>

                <Link asChild href={LINKS.ACCOUNT}>
                  <Navbar.Floating.TextLink onPress={handleMoreLinkPress}>
                    <FormattedMessage {...messages.myAccount} />
                  </Navbar.Floating.TextLink>
                </Link>
              </Navbar.Floating.PopupFooter>
            ) : null}
          </Navbar.Floating.Popup>
        ) : null}

        <Navbar.Floating.SecondaryNav>
          {isLoggedIn ? null : (
            <Link
              aria-label={formatMessage(messages.logInOrSignUp)}
              asChild
              href={LINKS.AUTH}
              onPress={handleMoreLinkPress}
            >
              <Navbar.Floating.AuthButton shadow={shouldShowGlassShadow}>
                <FormattedMessage {...messages.logInOrSignUp} />
              </Navbar.Floating.AuthButton>
            </Link>
          )}

          {shouldShowCurrentOrder && pendingOrder ? (
            <Navbar.Floating.CurrentOrder.Container
              shadow={shouldShowGlassShadow}
              title={`${pendingOrder.etaLabel} • ${pendingOrder.statusLabel}`}
            >
              <Link asChild href={pendingOrder.href}>
                <Navbar.Floating.CurrentOrder.LinkPressable onPress={handleCurrentOrderPress}>
                  {pendingOrder.actionLabel}
                </Navbar.Floating.CurrentOrder.LinkPressable>
              </Link>
            </Navbar.Floating.CurrentOrder.Container>
          ) : (
            <Link asChild href={LINKS.LOCATION_SELECTION} aria-label={locationTitle}>
              <Navbar.Floating.LocationButton
                eyebrow={locationEyebrow}
                shadow={shouldShowGlassShadow}
                title={locationTitle}
                onPress={handleMoreLinkPress}
              />
            </Link>
          )}

          <Link
            aria-label={formatMessage(messages.bagAccessibilityLabel, { count: bagCount })}
            asChild
            href={LINKS.BAG}
          >
            <Navbar.Floating.BagButton
              count={bagCount}
              shadow={shouldShowGlassShadow}
              onPress={handleBagPress}
            />
          </Link>
        </Navbar.Floating.SecondaryNav>
      </Navbar.Floating.Container>

      {shouldRenderStaticContainer ? <View style={staticNavbarContainerStyles} /> : undefined}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  staticNavbarContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  staticNavbarContainerBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  drawerAuthLabel: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationContent = {
  moreMenuNavLinks: Entry<NavigationLink>[];
};

type NavigationLink = {
  title: string;
  text?: string;
  url: string;
  image: Asset;
};
